import { useEffect, useState } from "react";
import type { FormEvent } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { useFavorites } from "@/hooks/useFavorites";

const NAV_ITEMS = [
  { label: "首页", to: "/" },
  { label: "电影", to: "/category/movie" },
  { label: "电视剧", to: "/category/tv" },
  { label: "综艺", to: "/category/variety" },
  { label: "关于我们", to: "/about" },
];

export default function SiteHeader() {
  const location = useLocation();
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [keyword, setKeyword] = useState("");
  const { favorites } = useFavorites();
  const isHome = location.pathname === "/";
  const solid = scrolled || !isHome || menuOpen;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname, location.search]);

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const q = keyword.trim();
    navigate(q ? `/search?q=${encodeURIComponent(q)}` : "/search");
    setMenuOpen(false);
  };

  const linkBase = "text-sm font-medium transition-colors";
  const linkColor = solid
    ? "text-foreground-700 hover:text-primary-600"
    : "text-white/85 hover:text-white";
  const activeColor = solid ? "!text-primary-600" : "!text-white";

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-300 ${
        solid ? "border-b border-background-200 bg-background-50/95 backdrop-blur-md" : "bg-gradient-to-b from-black/55 to-transparent"
      }`}
    >
      <div className="flex h-16 items-center gap-4 px-4 md:px-8">
        <Link to="/" className="flex shrink-0 items-center gap-2 whitespace-nowrap">
          <span className="flex h-9 w-9 items-center justify-center rounded-md bg-accent-500 text-base font-black text-primary-950">
            91
          </span>
          <span className={`text-lg font-bold tracking-tight ${solid ? "text-primary-800" : "text-white"}`}>
            在线观看视频
          </span>
        </Link>

        <nav className="ml-6 hidden items-center gap-7 md:flex">
          {NAV_ITEMS.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === "/"}
              className={({ isActive }) => `${linkBase} ${isActive ? activeColor : linkColor}`}
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="ml-auto flex items-center gap-2">
          <form
            onSubmit={handleSubmit}
            className="hidden h-9 items-center gap-2 rounded-full border border-background-300 bg-background-100/80 px-3 lg:flex"
          >
            <i className="ri-search-line text-sm text-foreground-500"></i>
            <input
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              type="text"
              name="q"
              placeholder="搜索影视作品"
              className="w-40 bg-transparent text-sm text-foreground-950 outline-none placeholder:text-foreground-500"
            />
          </form>

          <Link
            to="/favorites"
            aria-label="我的收藏"
            className={`relative flex h-9 w-9 items-center justify-center rounded-full transition-colors ${
              solid ? "text-foreground-700 hover:bg-background-200" : "text-white hover:bg-white/20"
            }`}
          >
            <i className="ri-heart-3-line text-lg"></i>
            {favorites.length > 0 && (
              <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-accent-500 px-1 text-[10px] font-bold text-primary-950">
                {favorites.length}
              </span>
            )}
          </Link>

          <button
            type="button"
            aria-label="打开菜单"
            onClick={() => setMenuOpen((v) => !v)}
            className={`flex h-9 w-9 items-center justify-center rounded-md md:hidden ${
              solid ? "text-foreground-900" : "text-white"
            }`}
          >
            <i className={menuOpen ? "ri-close-line text-xl" : "ri-menu-line text-xl"}></i>
          </button>
        </div>
      </div>

      {menuOpen && (
        <div className="border-t border-background-200 bg-background-50 px-4 pb-5 pt-4 md:hidden">
          <form
            onSubmit={handleSubmit}
            className="mb-4 flex h-10 items-center gap-2 rounded-full border border-background-300 bg-background-100 px-4"
          >
            <i className="ri-search-line text-sm text-foreground-500"></i>
            <input
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              type="text"
              placeholder="搜索影视作品"
              className="w-full bg-transparent text-sm text-foreground-950 outline-none placeholder:text-foreground-500"
            />
          </form>
          <nav className="flex flex-col">
            {NAV_ITEMS.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === "/"}
                className={({ isActive }) =>
                  `rounded-md px-3 py-2.5 text-sm font-medium ${
                    isActive ? "bg-primary-50 text-primary-700" : "text-foreground-700"
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>
        </div>
      )}
    </header>
  );
}